import type { FigureCategory } from '../types';

/** Short random id, matching the prototype's uid(). */
export const uid = (): string => Math.random().toString(36).slice(2, 9);

/** Format an integer-ish string with thousands separators. */
export const fmtN = (n: string | number | null | undefined): string => {
  const v = parseInt(String(n ?? ''), 10);
  return isNaN(v) ? '' : v.toLocaleString('en-US');
};

/** Format an ISO date into "Mon D, YYYY". */
export const fmtD = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Parse an ISO-8601 duration (PT#H#M#S) into whole seconds. */
export const durationToSeconds = (iso: string | null | undefined): number => {
  const m = (iso ?? '').match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!m) return 0;
  const h = m[1] ? parseInt(m[1], 10) : 0;
  const mi = m[2] ? parseInt(m[2], 10) : 0;
  const s = m[3] ? parseInt(m[3], 10) : 0;
  return h * 3600 + mi * 60 + s;
};

/** Format an ISO-8601 duration into H:MM:SS / M:SS. */
export const fmtDur = (iso: string | null | undefined): string => {
  const total = durationToSeconds(iso);
  if (!iso || total === 0) return '';
  const h = Math.floor(total / 3600);
  const mi = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h
    ? `${h}:${String(mi).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${mi}:${String(s).padStart(2, '0')}`;
};

/** Extract an 11-char YouTube video id from any common URL shape. */
export const parseYtId = (u: string | null | undefined): string | null => {
  const s = (u ?? '').trim();
  let m = s.match(/(?:[?&]v=|\/embed\/|youtu\.be\/|\/shorts\/|\/live\/)([A-Za-z0-9_-]{11})/);
  if (m && m[1]) return m[1];
  m = s.match(/(?:^|[^A-Za-z0-9_-])([A-Za-z0-9_-]{11})(?:$|[^A-Za-z0-9_-])/);
  return m && m[1] ? m[1] : null;
};

/** Resolve a channel reference (handle or id) from a URL/string. */
export const parseChannelRef = (u: string | null | undefined): { forHandle: string } | { id: string } | null => {
  const s = (u ?? '').trim();
  let m = s.match(/youtube\.com\/(@[A-Za-z0-9._-]+)/);
  if (m && m[1]) return { forHandle: m[1] };
  m = s.match(/youtube\.com\/channel\/(UC[A-Za-z0-9_-]{22})/);
  if (m && m[1]) return { id: m[1] };
  if (/^@/.test(s)) return { forHandle: s };
  if (/^UC[A-Za-z0-9_-]{22}$/.test(s)) return { id: s };
  return s ? { forHandle: '@' + s.replace(/^@/, '') } : null;
};

/** Best-effort category guess from an uploaded file name. */
export const guessCat = (name: string | null | undefined): FigureCategory => {
  const n = (name ?? '').toLowerCase();
  if (/banner|header|cover/.test(n)) return 'banner';
  if (/home|channel|feature/.test(n)) return 'homepage';
  if (/about|desc/.test(n)) return 'about';
  if (/grid|thumb.*grid|strip|uploads/.test(n)) return 'grid';
  if (/thumb|video/.test(n)) return 'video';
  return 'other';
};

/** First sentence of a block of text (used for the lean client report). */
export const firstSentence = (t: string | null | undefined): string => {
  const m = (t ?? '').match(/^[^.!?]*[.!?]/);
  return m ? m[0].trim() : (t ?? '').slice(0, 140);
};
